import { useState } from "react";
import styled from "styled-components";
import { useLocation } from "@entry/navigation";

import { bankLogo, Lock, Security } from "@assets";
import { Bank2 } from "@assets/svg/Bank2";
import { ErrorComponent } from "@components/ErrorComponent";
import { Loader } from "@components/Loader";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { entryColors, entryFont } from "@entry/tokens";
import { Spinner } from "@entry/ui";
import { useBankLinkingFlow, useBankLinkToken } from "@hooks";
import { i18n, Translations } from "@i18n";
import { BankLinkingRouteComponentProps, BankLinkingRouteProps } from "./types";

const CenterBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${entryFont.title};
  font-weight: 400;
  font-size: 26px;
  line-height: 1.28;
  color: ${entryColors.textPrimary};
  text-align: center;
`;

const Subtitle = styled.p`
  margin: 12px 0 0;
  font-family: ${entryFont.body};
  font-size: 15px;
  line-height: 1.5;
  color: ${entryColors.textSecondary};
  text-align: center;
`;

const GlassCard = styled.div`
  width: 100%;
  box-sizing: border-box;
  background: rgba(244, 241, 242, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 16px;
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
`;

const ItemRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

const ItemIcon = styled.div`
  flex-shrink: 0;
`;

const ItemContent = styled.div`
  margin-left: 12px;
  flex: 1;
`;

const ItemTitle = styled.div`
  font-family: ${entryFont.body};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  color: ${entryColors.textPrimary};
`;

const ItemSubtitle = styled.div`
  margin-top: 4px;
  font-family: ${entryFont.body};
  font-size: 13px;
  line-height: 1.45;
  color: ${entryColors.textSecondary};
`;

const Disclaimer = styled.p`
  margin: 0;
  font-family: ${entryFont.body};
  font-size: 11px;
  line-height: 13px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
`;

const FEATURE_ITEMS = [
  {
    icon: <Security />,
    title: i18n.t(Translations.BankLinkingRouteItem1Title),
    subtitle: i18n.t(Translations.BankLinkingRouteItem1Subtitle),
  },
  {
    icon: <Lock />,
    title: i18n.t(Translations.BankLinkingRouteItem2Title),
    subtitle: i18n.t(Translations.BankLinkingRouteItem2Subtitle),
  },
  {
    icon: <Bank2 />,
    title: i18n.t(Translations.BankLinkingRouteItem3Title),
    subtitle: i18n.t(Translations.BankLinkingRouteItem3Subtitle),
  },
];

export const BankLinkingRoute = () => {
  const location = useLocation();

  const bankLinkId = (location.state as BankLinkingRouteProps)?.bankLinkId;

  const { token, loading, error } = useBankLinkToken({
    input: { id: bankLinkId },
  });

  if (loading) return <Loader />;
  if (error || !token) return <ErrorComponent />;

  return <BankLinkingRouteComponent token={token} />;
};

const BankLinkingRouteComponent = ({ token }: BankLinkingRouteComponentProps) => {
  const [plaidOpen, setPlaidOpen] = useState(false);

  const { open, ready, isSendingToken } = useBankLinkingFlow({
    token,
    onPlaidClose: () => setPlaidOpen(false),
  });

  const onClick = () => {
    if (ready) {
      setPlaidOpen(true);
      open();
    }
  };

  const props: OnboardingLayoutProps = {
    cta: {
      primary: {
        title: i18n.t(Translations.BankLinkingRoutePrimaryCTA),
        onClick,
        loading: !plaidOpen && isSendingToken,
        disabled: plaidOpen || isSendingToken,
      },
    },
    footer: (
      <Disclaimer>{i18n.t(Translations.BankLinkingRouteDisclaimer)}</Disclaimer>
    ),
  };

  return (
    <OnboardingLayout {...props}>
      {plaidOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spinner style={{ width: 34, height: 34, borderWidth: 3 }} />
        </div>
      )}
      <div style={{ visibility: plaidOpen ? "hidden" : "visible" }}>
        <CenterBlock>
          <img src={bankLogo} alt="" width={118} height={68} />
          <Spacing size={SpacingSizes.XL} />
          <Title>{i18n.t(Translations.BankLinkingRouteTitle)}</Title>
          <Subtitle>{i18n.t(Translations.BankLinkingRouteSubtitle)}</Subtitle>
        </CenterBlock>
        <Spacing size={SpacingSizes.XL} />

        <GlassCard>
          {FEATURE_ITEMS.map((item, index) => (
            <div key={item.title}>
              {index > 0 && <Spacing size={SpacingSizes.M} />}
              <ItemRow>
                <ItemIcon>{item.icon}</ItemIcon>
                <ItemContent>
                  <ItemTitle>{item.title}</ItemTitle>
                  {item.subtitle && <ItemSubtitle>{item.subtitle}</ItemSubtitle>}
                </ItemContent>
              </ItemRow>
            </div>
          ))}
        </GlassCard>
      </div>
    </OnboardingLayout>
  );
};
